import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import { errorHandler } from '@autopass-web/domain';

import repo from '../service/repository';
import storage from '../service/storage';

const distributorStore = (set, get) => ({
  distributor: null,
  setDistributor: (payload) =>
    set({
      distributor: {
        type: payload.type,
        name: payload.name,
        methods: payload.methods(get),
        params: payload.params,
      },
    }),
});

const userStore = (set, get) => ({
  initialize: false,
  initializeApp: () => set({ initialize: true }),
  me: null,
  licensePlates: [],
  bootstraps: null,
  parkingRecord: [],
  promotionBoard: [],
  orders: {
    refueling: [],
    parking: [],
  },
  updateApp: (payload) =>
    set((state) => {
      let refuelingOrders = state.orders?.refueling ?? [];
      let parkingOrders = state.orders?.parking ?? [];
      let parkingRecords = payload.parkingRecord || [];
      let licensePlates = payload.licensePlates || [];
      storage.saveUser(payload.me);
      return {
        me: payload.me,
        licensePlates: licensePlates,
        parkingRecord: parkingRecords,
        promotionBoard: payload.promotionBoard,
        orders: {
          refueling: [...state.orders.refueling, ...refuelingOrders],
          parking: [...state.orders.parking, ...parkingOrders],
        },
      };
    }),
  updateUser: (payload) =>
    set(() => {
      storage.saveUser(payload);
      return { me: payload };
    }),
  updateLicensePlate: (payload) => set({ licensePlates: payload }),
  updateParkingRecord: async () => {
    const { error, data } = await repo.fetchParkingRecord();
    if (error) {
      set({ parkingRecord: [] });
      get().showFailMessage(error);
      return;
    }
    set({ parkingRecord: data });
  },
  updateBootstrap: (payload) => set({ bootstraps: payload }),
});

const refuelingRequestStore = (set, get) => ({
  refuelingRequest: null,
  isRefuelingRequestProcessing: false,
  setRefuelingRequest: (refuelingRequest) => set({ refuelingRequest }),
  toggleRefuelingProcessingDialog: () =>
    set((state) => ({
      isRefuelingRequestProcessing: !state.isRefuelingRequestProcessing,
    })),
  rejectRefuelingRequest: async (id) => {
    const { error } = await repo.answerRefuelingRequests({
      id,
      answer: 'reject',
    });
    if (error) {
      get().showFailMessage(error);
    } else {
      get().setToastMessage('此筆授權交易已取消成功');
    }

    get().setRefuelingRequest(null);
  },
  acceptRefuelingRequest: async (id) => {
    get().toggleRefuelingProcessingDialog();

    const { error, data } = await repo.answerRefuelingRequests({
      id,
      answer: 'accept',
    });

    if (error) {
      get().showFailMessage('付款失敗，請確認付款資訊是否有效或洽詢客服');
    } else {
      if (data.state === 'accepted' && data.paymentState === 'paid') {
        get().showSuccessMessage('加油付款成功！別忘了下次也要用麻吉付喔～');
      } else if (data.state === 'rejected' && data.paymentState === 'unpaid') {
        get().showFailMessage('此筆授權交易已取消成功');
      } else {
        get().showFailMessage('付款失敗，請確認付款資訊是否有效或洽詢客服');
      }
    }

    get().setRefuelingRequest(null);
    get().toggleRefuelingProcessingDialog();
  },
  refuelingRequestTimeout: () => {
    get().setRefuelingRequest(null);
    get().setToastMessage('授權時間已逾時，請重新再試。');
  },
});

const messageStore = (set, get) => ({
  appMessage: {
    type: '',
    message: '',
    onClick: null,
  },
  appMessageShow: false,
  toggleAppMessage: (payload) =>
    set((state) => ({ appMessageShow: payload ?? !state.appMessageShow })),
  showSuccessMessage: (message, onClick = null) => {
    get().toggleAppMessage();
    set({
      appMessage: { type: 'success', message, onClick },
    });
  },
  showFailMessage: (error, onClick = null) => {
    get().toggleAppMessage();
    const { message } = errorHandler(error);
    set({
      appMessage: {
        type: 'failure',
        message,
        onClick,
      },
    });
  },
  toastMessage: '',
  setToastMessage: (payload) => set({ toastMessage: payload }),
});

const linebankStore = (set) => ({
  lineBankLoginDialogShow: false,
  toggleLineBankLoginDialog: () =>
    set((state) => ({
      lineBankLoginDialogShow: !state.lineBankLoginDialogShow,
    })),
});

const customerStore = (set) => ({
  customerServiceDialog: false,
  toggleCustomerServiceDialog: () =>
    set((state) => ({ customerServiceDialog: !state.customerServiceDialog })),
});

const marketingEmailOptedInDialogStore = (set) => ({
  isShowMarketingEmailOptedInDialog: false,
  setIsShowMarketingEmailOptedInDialog: (value) =>
    set((state) => ({ isShowMarketingEmailOptedInDialog: value })),
});

const useAppStore = create(
  subscribeWithSelector((set, get) => ({
    ...distributorStore(set, get),
    ...userStore(set, get),
    ...refuelingRequestStore(set, get),
    ...messageStore(set, get),
    ...linebankStore(set),
    ...customerStore(set),
    ...marketingEmailOptedInDialogStore(set),
  })),
);

export default useAppStore;
